import React from 'react';
import './Snippet.less';
import {
  generateSkuDescription,
  generateMatrixSkuName,
  stripProductSkuShortLabel,
} from 'util/sku-formatter';
import {
  tradePriceForSku,
  normalizeMailchimpPrice,
  getFoodstuffsNi,
  getFoodstuffsSi,
  isFoodstuffsPricingType,
} from 'util/mailchimpSkuPrice';
import { isKegSku } from 'util/pricing-fields';

function skuDisplayLine(sku, productName) {
  const unitType = sku.unit_type ?? sku.unitType;
  const packType = sku.pack_type ?? sku.packType;
  const caseType = sku.case_type ?? sku.caseType;
  const containerType = sku.container_type ?? sku.containerType;
  const fd = sku.format_description ?? sku.formatDescription;
  const ct = String(containerType || '').toLowerCase();
  if (ct && unitType != null && unitType !== '') {
    const matrix = generateMatrixSkuName({
      productName: '',
      containerType: ct,
      unitType: String(unitType),
      packType: String(packType ?? ''),
      caseType: String(caseType ?? ''),
    });
    if (matrix) return matrix;
  }
  if (fd) return fd;
  const mapped = {
    unitType, packType, caseType, containerType,
  };
  const d = generateSkuDescription(mapped);
  if (d) return d;
  if (ct === 'keg') {
    return generateMatrixSkuName({
      productName: '',
      containerType: 'keg',
      unitType: String(unitType ?? ''),
      packType,
      caseType,
    });
  }
  return stripProductSkuShortLabel(sku.name || '', productName || '');
}

/**
 * Heading bucket for email column (legacy-style labels).
 * @param {object} sku
 * @returns {string}
 */
function skuFormatHeading(sku) {
  if (isKegSku(sku)) return 'KEG';
  const ct = String(sku.container_type ?? sku.containerType ?? '').toLowerCase();
  if (ct === 'bottle') return 'CASE';
  if (ct === 'can') return 'SLAB';
  return 'PACK';
}

function priceWithDollar(raw) {
  const n = normalizeMailchimpPrice(raw);
  if (n == null) return '—';
  const s = String(n).trim();
  if (s.startsWith('$')) return s;
  return `$${s}`;
}

/**
 * Trade price or Foodstuffs SAP for the tier (no $ on SAP).
 * @param {string} pricingType
 * @param {object} sku
 * @returns {import('react').ReactNode}
 */
function tierPriceOrSapCell(pricingType, sku) {
  if (pricingType === 'foodstuffsBoth') {
    const ni = getFoodstuffsNi(sku);
    const si = getFoodstuffsSi(sku);
    if (!ni && !si) return '—';
    return (
      <>
        {ni ? <div>{`NI: ${ni}`}</div> : null}
        {si ? <div>{`SI: ${si}`}</div> : null}
      </>
    );
  }
  if (isFoodstuffsPricingType(pricingType)) {
    return tradePriceForSku(sku, pricingType) ?? '—';
  }
  return priceWithDollar(tradePriceForSku(sku, pricingType));
}

/**
 * @param {object} product
 * @param {'tapBadge'|'webRender'|undefined} source — undefined keeps legacy web-first behaviour.
 */
function heroImageForProduct(product, source) {
  const web = product.webRenderUrl;
  const tap = product.tapBadgeUrl;
  const webStyle = { maxWidth: '180px', maxHeight: '180px', width: 'auto' };
  const tapStyle = { maxWidth: '140px' };

  if (source == null) {
    const src = web || tap;
    return { src, style: web ? webStyle : tapStyle };
  }
  if (source === 'webRender') {
    if (web) return { src: web, style: webStyle };
    if (tap) return { src: tap, style: tapStyle };
    return { src: '', style: tapStyle };
  }
  if (tap) return { src: tap, style: tapStyle };
  if (web) return { src: web, style: webStyle };
  return { src: '', style: tapStyle };
}

const ProductSnippetSku = ({ pricingType, product, productImageSource }) => {
  const {
    name, oneLiner, abv, abvMin, abvMax, blurb, webRenderUrl, tapBadgeUrl,
  } = product;
  const skus = product.includedSkus || [];

  const { src: heroSrc, style: heroStyle } = heroImageForProduct(product, productImageSource);

  const filteredName = name?.replace(/â€˜|â€™/g, "'");
  const filteredBlurb = blurb?.replace(/â€˜|â€™/g, "'");
  const filteredOneLiner = oneLiner?.replace(/â€˜|â€™/g, "'");

  return (
    <div
      className="email-snippet"
      style={{
        padding: '20px 0px',
        width: '600px',
        margin: '0 auto',
        borderTop: '1px solid #E5E5E5',
        borderBottom: '1px solid #E5E5E5',
        backgroundColor: 'white',
      }}
    >
      <table align="center" style={{ maxWidth: '600px' }}>
        <tbody>
          <tr>
            <td
              width="180"
              style={{
                padding: '5px',
                backgroundColor: 'white',
                textAlign: 'center',
              }}
            >
              <img
                alt={`${filteredName}`}
                src={heroSrc || webRenderUrl || tapBadgeUrl}
                style={heroStyle}
              />
            </td>
            <td
              style={{
                backgroundColor: 'white',
                padding: '15px !important',
              }}
            >
              <h2>{name}</h2>
              <h3 id="beertitle">
                {`${(() => {
                  const abvIsRange = abvMin !== null || abvMax !== null;

                  if (abv === null && !abvIsRange) {
                    return 'N/A';
                  }

                  return (abv !== null ? (
                    `${abv}%`
                  ) : (
                    `${abvMin === null ? '?' : `${abvMin}%`} - ${abvMax === null ? '?' : `${abvMax}%`}`
                  ));
                })()}`}
                {' '}
                -
                {' '}
                {filteredOneLiner}
              </h3>
              <p contentEditable="">{filteredBlurb}</p>
            </td>
          </tr>
        </tbody>
      </table>
      {skus.length === 0 ? (
        <p style={{ textAlign: 'center', padding: '12px', color: '#888' }}>
          No formats selected — choose SKUs in the generator to show trade pricing.
        </p>
      ) : (
        <table align="center" style={{ maxWidth: '600px', width: '100%' }}>
          <tbody>
            <tr>
              {skus.map((sku) => {
                const tierCell = tierPriceOrSapCell(pricingType, sku);
                const desc = skuDisplayLine(sku, name);
                const heading = skuFormatHeading(sku);
                return (
                  <td
                    key={sku.key}
                    style={{
                      padding: '5px',
                      backgroundColor: 'white',
                      textAlign: 'center',
                      verticalAlign: 'top',
                    }}
                  >
                    <h4
                      className="contents"
                      style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        margin: '0px',
                      }}
                    >
                      {heading}
                    </h4>
                    {desc && (
                      <h6 style={{ fontSize: '12px', fontWeight: 'normal', margin: '4px 0' }} contentEditable="">
                        {desc}
                      </h6>
                    )}
                    <h5
                      contentEditable=""
                      style={{
                        backgroundColor: '#F7F7F7',
                        padding: '7px',
                        margin: '2px',
                        fontSize: '18px',
                        fontWeight: '300',
                        textAlign: 'center',
                      }}
                    >
                      {tierCell}
                    </h5>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ProductSnippetSku;
